import React, { useMemo } from 'react';
import cl from 'classnames';
import { HOTELS_VIEW_MODE } from 'src/constants';
import { setLocalStorageItem } from 'src/helper';
import { useLanguage } from 'src/hooks';
import { Select } from 'src/components/general';
import styles from './viewModeSwitcherStyle.module.scss';

export type ViewMode = 'list' | 'cards';

const Icon: React.FC<{ viewMode: ViewMode; isActive: boolean }> = ({
  viewMode,
  isActive,
}) => {
  switch (viewMode) {
    case 'list': {
      return (
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <rect
            x="5.71387"
            width="10.2857"
            height="2.96297"
            rx="1.48148"
            fill={isActive ? '#1F93FF' : '#FFF'}
          />
          <rect
            x="5.71387"
            y="13.0371"
            width="10.2857"
            height="2.96297"
            rx="1.48148"
            fill={isActive ? '#1F93FF' : '#FFF'}
          />
          <rect
            y="13.0371"
            width="4.28572"
            height="2.96297"
            rx="1.48148"
            fill={isActive ? '#1F93FF' : '#FFF'}
          />
          <rect
            y="6.51855"
            width="4.28572"
            height="2.96297"
            rx="1.48148"
            fill={isActive ? '#1F93FF' : '#FFF'}
          />
          <rect
            width="4.28572"
            height="2.96297"
            rx="1.48148"
            fill={isActive ? '#1F93FF' : '#FFF'}
          />
          <rect
            x="5.71387"
            y="6.51807"
            width="10.2857"
            height="2.96297"
            rx="1.48148"
            fill={isActive ? '#1F93FF' : '#FFF'}
          />
        </svg>
      );
    }
    case 'cards': {
      return (
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <rect
            width="7.27273"
            height="7.27273"
            rx="2"
            fill={isActive ? '#1F93FF' : '#FFF'}
          />
          <rect
            y="8.72705"
            width="7.27273"
            height="7.27273"
            rx="2"
            fill={isActive ? '#1F93FF' : '#FFF'}
          />
          <rect
            x="8.72754"
            width="7.27273"
            height="7.27273"
            rx="2"
            fill={isActive ? '#1F93FF' : '#FFF'}
          />
          <rect
            x="8.72754"
            y="8.72705"
            width="7.27273"
            height="7.27273"
            rx="2"
            fill={isActive ? '#1F93FF' : '#FFF'}
          />
        </svg>
      );
    }
  }
};

type SwitcherProps = {
  viewMode: ViewMode;
  setViewMode: (value: ViewMode) => void;
  className?: string;
};

export const Switcher = ({
  viewMode,
  setViewMode,
  className,
}: SwitcherProps) => {
  const { t } = useLanguage();

  const items: { value: ViewMode; label: string }[] = useMemo(
    () => [
      {
        value: 'list',
        label: t('page.hotels.switcher.list'),
      },
      {
        value: 'cards',
        label: t('page.hotels.switcher.cards'),
      },
    ],
    [t],
  );

  return (
    <div className={cl(styles.container, className)}>
      <div className={styles.buttons}>
        {items.map(({ value, label }) => (
          <button
            key={`vm-swBtn-${value}`}
            className={cl(
              styles.button,
              styles[value],
              value === viewMode && styles.current,
            )}
            type="button"
            onClick={() => {
              setViewMode(value);
              setLocalStorageItem<ViewMode>(HOTELS_VIEW_MODE, value);
            }}
          >
            <div className={styles.icon}>
              <Icon viewMode={value} isActive={value === viewMode} />
            </div>
            <div className={styles.label}>{label}</div>
          </button>
        ))}
      </div>
      <div className={styles.mobileGroup}>
        <Select
          value={viewMode}
          items={items}
          selectItem={(value) => {
            setViewMode(value as ViewMode);
            setLocalStorageItem<ViewMode>(HOTELS_VIEW_MODE, value as ViewMode);
          }}
          design="switcherBorder"
          icon={<Icon viewMode={viewMode} isActive />}
          excludeCurrent
          className={styles.select}
        />
      </div>
    </div>
  );
};

export default Switcher;
